<template>
  <div class="for-you-page pt-0">
    <ClientOnly>
      <MoneyBackCard />
    </ClientOnly>
    <MarketingCarousel
      :slides="marketingCarouselSlides"
      @images-settled="handleCarouselImagesSettled"
      @slide-click="handleSlideClick"
    />
    <NuxtLayout name="teasers" v-bind="{ loading }" :no-results="!elements.length">
      <component
        :is="component"
        v-for="({ component, element, key, props }, index) in items"
        v-bind="{ key, ...props }"
        :fetchpriority="index < priorityFetchAmount ? 'high' : 'low'"
        @click="handleElementClick(element)"
      />
    </NuxtLayout>

    <HighFrequencyGameExploreMoreCarousel :variants="['draw', 'scratch', 'keno']" />
  </div>
</template>

<script lang="ts" setup>
import type {
  RecommendationElement,
  TeaserPayload,
} from '#api/shared/@types/generated/backend/graphql-schema-types'

import { HighFrequencyGameExploreMoreCarousel } from '#components'
import { FREE_TO_PLAY_ITEM_NAME } from '#free-to-play/app/constants/constants'

import type { MarketingCarouselSlideProps } from '../../../components/MarketingCarousel/MarketingCarousel.vue'

import { useFetchOptions } from '../utils/fetchOptions'

const CAROUSEL_LIST_NAME = 'Home Marketing Carousel'
const CAROUSEL_LIST_ID = 'home_marketing_carousel'

const { locale } = useI18n()
const licensedTerritory = useLicensedTerritory({ raw: true })
const isKenoEnabled = useIsFeatureFlagEnabled('keno')
const { clientUrl } = useClientUrl()
const requestUrl = useRequestURL()
const { isDesktop } = useDevice()

const priorityFetchAmount = computed(() => (isDesktop ? 6 : 3))

const { loading, result: recommendationElementsResult } = useRecommendationElementsQuery(
  { licensedTerritory },
  useFetchOptions(),
)

const { result: marketingCarouselResult } = useMarketingCarouselQuery(
  { licensedTerritory },
  useFetchOptions(),
)

const marketingCarouselSlides = computed(() =>
  mapMarketingCarouselSlides(marketingCarouselResult.value?.marketingCarousel ?? [], locale.value, {
    backend: clientUrl.origin,
    site: requestUrl.host,
  }),
)

const recommendationElements = computed(() => {
  const result = recommendationElementsResult.value?.recommendationElements
  return isGraphqlSuccess(result) ? (result.elements as RecommendationElement[]) : undefined
})

const TRACKING_LIST_NAME = 'Home Lottery Teasers'
const TRACKING_LIST_ID = 'home_lottery_teasers'

const { createTeaserListItems } = useTrackingData()
const { pushSelectListItemEvent, pushViewListEvent } = useTracking()
const { logTrackingError } = useTrackingErrorLog()

const elements = computed(
  () =>
    recommendationElements.value?.filter(({ __typename }) => {
      if (isKenoEnabled) return true
      return __typename !== 'HighFrequencyTeaserPayload'
    }) ?? [],
)
const trackingTeaserListItems = ref<ReturnType<typeof createTeaserListItems>>([])

const items = computed(() =>
  mapTeaserElements(elements.value, {
    licensedTerritory,
    locale: locale.value,
  }),
)

const handleElementClick = (element: RecommendationElement) => {
  const teaserItem = trackingTeaserListItems.value.find((item) => {
    switch (element.__typename) {
      case 'FreeToPlayTeaserPayload':
        return item.item_name === FREE_TO_PLAY_ITEM_NAME
      case 'LotteryTeaserPayload':
        return item.item_id === element.id
      default:
        return false
    }
  })

  if (!teaserItem) return

  try {
    pushSelectListItemEvent({
      items: [teaserItem],
      list_id: TRACKING_LIST_ID,
      list_name: TRACKING_LIST_NAME,
    })
  } catch (err) {
    logTrackingError('select_list_item', err)
  }
}

const handleSlideClick = (slide: MarketingCarouselSlideProps, index: number) => {
  try {
    pushSelectListItemEvent({
      items: [toCarouselListItem(slide, index)],
      list_id: CAROUSEL_LIST_ID,
      list_name: CAROUSEL_LIST_NAME,
    })
  } catch (err) {
    logTrackingError('select_list_item', err)
  }
}

const toCarouselListItem = (slide: MarketingCarouselSlideProps, index: number) => ({
  index: index + 1,
  item_name: slide.trackingName,
})

const handleCarouselImagesSettled = (slides: MarketingCarouselSlideProps[]) => {
  if (!slides.length) return

  try {
    pushViewListEvent({
      items: slides.map(toCarouselListItem),
      list_id: CAROUSEL_LIST_ID,
      list_name: CAROUSEL_LIST_NAME,
    })
  } catch (err) {
    logTrackingError('view_list', err)
  }
}

let unwatchElements: Maybe<() => void> = null
unwatchElements = watchImmediate(elements, (list) => {
  if (!list || list.length === 0) return

  try {
    const filteredTeasers = elements.value.filter(
      (teaser) => teaser.__typename && teaser.__typename !== 'ExploreElement',
    ) as TeaserPayload[]
    trackingTeaserListItems.value = createTeaserListItems(filteredTeasers)
    pushViewListEvent({
      items: trackingTeaserListItems.value,
      list_id: TRACKING_LIST_ID,
      list_name: TRACKING_LIST_NAME,
    })

    if (unwatchElements) unwatchElements()
  } catch (err) {
    logTrackingError('view_list', err)
  }
})
</script>

<style lang="scss" scoped>
.for-you-page :deep(.teasers) {
  padding-bottom: token('size/space/gap/200');
}
</style>
